import { IconButton, Box, Flex } from "@chakra-ui/react";
import type { Filters } from "@superbright/indexeddb-orm";

import type { Questionnaire } from "api/schemas";
import { Button, Icon, Typography, PoweredBy } from "components";
import { FilterMultiSelectOptions } from "features/filters";
import { FilterPrice } from "features/filters/components/FilterPrice";

import styles from "../DesktopQuestionnaire/DesktopQuestionnaire.module.css";

import { ProgressIndicator } from "./ProgressIndicator";

type StoreType = {
  tempFilters: Filters & Record<string, unknown>;
  handleTempFilterChange: (key: string, value: unknown) => void | Promise<void>;
};

type PropertyData = {
  name?: string;
  _count?: { units?: number };
};

type Props = {
  question?: Questionnaire;
  store: StoreType;
  onNext: () => void;
  onLastStepContinue?: () => void;
  variant?: "desktop" | "mobile";
  currentStep?: number;
  totalSteps?: number;
  onBack?: (question?: Questionnaire) => void;
  isWelcomeStep?: boolean;
  isComplete?: boolean;
  propertyData?: PropertyData | null;
  isMobile?: boolean;
  onViewAllUnitsClick?: () => void;
  handleSkip: (question: Questionnaire) => void;
  isWelcomeOrComplete?: boolean;
};

const normalizeBodyKey = (bodyKey?: string) => {
  switch (bodyKey) {
    case "availability":
      return "date_availability";
    case "bedrooms":
      return "qty_bedrooms";
    case "cost":
      return "base_price";
    default:
      return bodyKey;
  }
};

export const QuestionStepRenderer = ({
  question,
  store,
  onNext,
  onLastStepContinue: _onLastStepContinue,
  variant: _variant = "desktop",
  currentStep,
  totalSteps,
  onBack,
  isWelcomeStep,
  isComplete,
  propertyData,
  isMobile,
  onViewAllUnitsClick,
  handleSkip,
  isWelcomeOrComplete,
}: Props) => {
  // Not welcome step: Render Progress, Back, and Question
  return (
    <Flex
      css={{
        position: "relative",
      }}
      width="100%"
      flexDirection="column"
      flex={1}
      justifyContent={isWelcomeStep ? "center" : "normal"}
      className={`${isWelcomeStep ? styles.welcomeWrapper : ""} ${
        isMobile ? styles.mobileWrapper : styles.desktopWrapper
      }`}
    >
      {/* Progress & Back */}
      <Box>
        {isWelcomeStep && (
          <Flex
            flexDirection="column"
            gap={2}
            align="center"
            justify="center"
            mb={isMobile ? 2 : 0}
          >
            <Typography variant={isMobile ? "h2" : "h1"}>
              {propertyData?.name
                ? `Welcome to ${propertyData.name}`
                : "Welcome"}
            </Typography>
            <Box mb={isMobile ? 4 : 0}>
              <Typography
                variant={isMobile ? "small" : "body"}
                weight={isMobile ? undefined : "medium"}
                color="muted"
              >
                Answer a few questions to help us {isMobile ? <br /> : null}{" "}
                find your perfect apartment
              </Typography>
            </Box>
            {isMobile && (
              <Box
                css={{
                  width: "100%",
                  mb: 2,
                }}
              >
                <Button
                  color="black"
                  variant="primary"
                  onClick={onNext}
                  isFullWidth
                >
                  Find My Perfect Apartment
                </Button>
              </Box>
            )}
          </Flex>
        )}

        {!isWelcomeStep && (
          <Box>
            <Flex flexDirection="column" transition="all 0.3s ease-in-out">
              <ProgressIndicator
                currentStep={currentStep ?? 0}
                totalSteps={totalSteps ?? 0}
                onSkip={() => question && handleSkip(question)}
              />
              {!isComplete && onBack && (
                <IconButton
                  aria-label="Back"
                  height={6}
                  width={6}
                  minWidth={6}
                  placeSelf="start"
                  onClick={() => onBack(question)}
                  variant="ghost"
                  color="var(--color-text)"
                  _hover={{ bg: "transparent", opacity: 0.7 }}
                  mt={!isMobile ? 2 : 0}
                >
                  <Icon name="arrow-left" />
                </IconButton>
              )}
            </Flex>
          </Box>
        )}
      </Box>
      {/* Question step rendering */}
      {question &&
        (() => {
          if (question.component === "slider") {
            return (
              <div className={styles.base}>
                <FilterPrice
                  value={store.tempFilters.base_price ?? 15000}
                  onChange={(v) =>
                    store.handleTempFilterChange("base_price", v)
                  }
                  onApply={onNext}
                  isQuestionnaire
                  min={question.options[0]?.min ?? 0}
                  max={question.options[0]?.max ?? 0}
                />
              </div>
            );
          }

          return (
            <div className={`${styles.base} ${styles.filterAvailability}`}>
              <FilterMultiSelectOptions
                label={question.label}
                subtitle={question.helper_text ?? undefined}
                options={question.options.map((o) => ({
                  ...o,
                  label: o.label || "",
                  value: o.value != null ? String(o.value) : "",
                }))}
                onChange={(selectedIndices) => {
                  // Store both index and value for each selected option
                  const newValue = selectedIndices.map((idx: number) => ({
                    index: idx,
                    bodyKey: normalizeBodyKey(question.body_key),
                    value: question.options[idx]?.value,
                    label: question.options[idx]?.label,
                  }));
                  store.handleTempFilterChange(question.name, newValue);
                }}
                onApply={onNext}
                // Pass only indices to the component for selection display
                values={(
                  (store.tempFilters[question.name] as Array<{
                    index: number;
                  }>) || []
                ).map((item) => item.index)}
                optionsVariant={
                  (question.options_variant || "grid") as "wrap" | "grid"
                }
                isQuestionnaire
                max={question.max}
                min={question.min}
              />
            </div>
          );
        })()}
      {isWelcomeOrComplete && (
        <Box mt={isMobile ? 0 : 8}>
          <Button variant="text" onClick={onViewAllUnitsClick}>
            <span>
              View all units{" "}
              {propertyData?._count?.units
                ? `(${propertyData._count.units})`
                : ""}
            </span>
          </Button>
        </Box>
      )}
      <Flex justifyContent="center">
        <PoweredBy className=" mt-[30px] sm:mt-0 w-[80px] h-[31px]  sm:h-[42px]" />
      </Flex>
    </Flex>
  );
};
